import { useRef, useState } from 'react';
import BaseDatePicker from 'react-date-picker';
import 'react-date-picker/dist/DatePicker.css';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import { withErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker';
import cs from 'classnames';
import { addYears, subYears } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

import { ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import { formatShortWeekday } from '../Calendar/lib/formatShortWeekday';
import Icon from '../Icon/Icon';
import ClearIcon from '../shared/ClearIcon';
import { default as Header } from '../shared/Header';
import Hint from '../shared/Hint';

import { useCalendarPosition } from './hooks/useCalendarPosition';
import { useYearChange } from './hooks/useYearChange';
import { getDateFormat } from './lib/formatDates';
import { parseDateValue } from './lib/parseDateValue';
import { validateDate } from './lib/validateDate';

import './lib/DateInputPicker.scss';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';

/**
 * @param {import('./lib/propTypes').DatePickerProps} props
 */

const DateInputPicker = (props) => {
  const {
    value,
    onChange = () => {},
    isTime = false,
    isRange = false,
    isTimeRange = false,
    isDateOnly = false,
    isTimezone = isTime || isTimeRange || false,
    isSeconds = false,
    minDate = subYears(new Date(), 150),
    maxDate = addYears(new Date(), 150),
    disabled = false,
    buttonClassName,
    parentClassName,
    isButtonTextWhite,
    isClearable = true,
    isIcon = true,
    iconIsRight = false,
    isCompact = false,
    showCalendarWeekNumbers = false,
    hideCalendar = false,
    position = 'left',
    error = null,
    ...headerProps
  } = props;

  const calendarRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const isValue = ia(value) ? Boolean(value?.[0]) : Boolean(value);
  const { pathname } = useLocation();
  const isSelfScheduling = pathname.startsWith('/book');
  const timezoneFromHook = useTimezone({ enabled: !isSelfScheduling });
  const timezone = isSelfScheduling
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : timezoneFromHook;
  const [activeStartDate, setActiveStartDate] = useState(() => {
    if (!isValue) return new Date();
    const raw = ia(value) ? value[0] : value;
    return parseDateValue(raw, timezone) || new Date();
  });
  const [activeView, setActiveView] = useState('month');

  if ((isTime || isTimeRange) && !isTimezone)
    throw new Error('isTimezone must be true when isTime or isTimeRange is true.');

  if ((isTime && isTimeRange) || (isRange && isTimeRange) || (isTime && isRange))
    throw new Error('isTime, isRange or isTimeRange cannot be true at the same time.');

  const DatePickerWrapper = isTime
    ? DateTimePicker
    : isRange
      ? DateRangePicker
      : isTimeRange
        ? DateTimeRangePicker
        : BaseDatePicker;

  const onCalendarClose = () => {
    setIsOpen(false);
    setActiveStartDate(value ? parseDateValue(value, timezone) || new Date() : new Date());
    setActiveView('month');
  };

  useCalendarPosition({ calendarRef, isOpen, onClose: onCalendarClose, position });

  useYearChange({ value, isRange, isOpen, calendarRef });

  const onResetDate = () => {
    onChange(null);
    setActiveStartDate(new Date());
    setActiveView('month');
  };

  const pickerDateToUtc = (pickerDate, tz) => {
    if (!pickerDate) return null;
    return fromZonedTime(pickerDate, tz).toISOString();
  };

  const toDateOnlyString = (date) => {
    if (!date) return null;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  };

  const handleChange = (date) => {
    if (!date) {
      onChange(null);
      setActiveStartDate(new Date());
      return;
    }
    if (isRange || isTimeRange) {
      const [startDate, endDate] = date;
      const finalDate = [pickerDateToUtc(startDate, timezone), pickerDateToUtc(endDate, timezone)];
      onChange(finalDate);
      setActiveStartDate(startDate || new Date());
    } else if (isDateOnly) {
      onChange(toDateOnlyString(date));
      setActiveStartDate(date || new Date());
    } else {
      const finalDate = pickerDateToUtc(date, timezone);
      onChange(finalDate);
      setActiveStartDate(date || new Date());
    }
  };

  const displayValue = (value, timezone) => {
    if (isRange || isTimeRange) {
      if (!value) return [null, null];
      const [startUtc, endUtc] = value;
      return [parseDateValue(startUtc, timezone), parseDateValue(endUtc, timezone)];
    } else {
      if (!value) return null;
      return parseDateValue(value, timezone);
    }
  };

  return (
    <div
      className={cs(
        'custom-picker w-full',
        parentClassName,
        disabled && 'cursor-not-allowed select-none'
      )}>
      <Header {...headerProps} />
      <DatePickerWrapper
        isOpen={isOpen}
        isCalendarOpen={isOpen}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={onCalendarClose}
        disableCalendar={hideCalendar && !isIcon}
        calendarProps={{
          inputRef: calendarRef,
          activeStartDate: validateDate(activeStartDate),
          onActiveStartDateChange: ({ activeStartDate }) => setActiveStartDate(activeStartDate),
          view: activeView,
          onViewChange: ({ view }) => setActiveView(view),
          className: cs('mt-2 is-border', isCompact && 'is-compact'),
          prev2Label: null,
          next2Label: null,
          prevLabel: <Icon icon="chevron-left-linear" size={16} />,
          nextLabel: <Icon icon="chevron-right-linear" size={16} />,
          formatShortWeekday,
          showWeekNumbers: showCalendarWeekNumbers
        }}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        format={getDateFormat({ isTime, isTimeRange, isSeconds })}
        calendarIcon={isIcon && <Icon icon="calendar-mono" size={20} />}
        clearIcon={
          <ClearIcon
            isClearable={isClearable ? isValue : false}
            value={value}
            onClick={onResetDate}
            color={isButtonTextWhite ? 'white' : 'neutral'}
            isValue={isValue}
          />
        }
        disableClock
        dayPlaceholder="dd"
        monthPlaceholder="mm"
        yearPlaceholder="yyyy"
        locale="en-US"
        {...props}
        id="date-input-picker"
        className={cs(
          isValue && 'filled',
          !iconIsRight && 'iconIsRight',
          isTimeRange && !isOpen && 'closed',
          pathname === '/iframe.html' && 'isDocumentation',
          isButtonTextWhite && 'white-text',
          buttonClassName
        )}
        onChange={(date) => handleChange(date)}
        value={displayValue(value, timezone)}
      />
      <Hint hint={error} error={error} icon={false} />
    </div>
  );
};

export default withErrorBoundary(DateInputPicker);
